import Twilio from 'twilio-chat/dist/twilio-chat.min';
import { AccessManager } from 'twilio-common/dist/twilio-common.min';

import Fuse from 'fuse.js';
import moment from 'moment-timezone';
import _ from 'lodash';

import { inject } from '../utils/index';
import events from '../events';

const defaultCounters = {
  total: 0,
  due_in: 0,
  checked_in: 0,
  due_out: 0,
  in_house: 0,
  checked_out: 0,
};

const defaultState = {
  ready: false,
  isInitializing: false,
  isLoading: false,
  hasError: false,
  channels: [],
  reservations: [],
  counters: { ...defaultCounters },
};

class TwilioController {
  constructor(mojito, stateFactory, gemsService, session) {
    this.mojito = mojito;
    this.channelsState = stateFactory('twilio', defaultState);
    this.session = session;
    this.gemsService = gemsService;
    this.activeChannel = null;

    this.requestSyncHanlder = _.throttle(this.syncChannelCounters.bind(this),
      5 * 1000, { leading: true, trailing: false });
  }

  get isReady() {
    const { isInitializing } = this.channelsState.value;
    return !isInitializing && this.twilio;
  }

  requestSync() {
    if (this.isReady) {
      this.requestSyncHanlder();
    }
  }

  listenEvents() {
    events.on('messages:close-override', async () => {
      this.activeChannel = null;
      await this.createClient();
    });
  }

  async initialize() {
    this.listenEvents();
    await this.mojito.ensureUserExist();
    await this.createClient();
  }

  getState() {
    return this.channelsState;
  }

  disconnectClient() {
    if (this.twilio) {
      this.twilio.removeAllListeners();
      this.twilio.shutdown();
    }

    if (this.accessManager) {
      this.accessManager.removeAllListeners();
    }
  }

  async createClient() {
    this.disconnectClient();

    this.channelsState.update({ isInitializing: true });

    const { token } = await this.mojito.getToken();

    this.accessManager = new AccessManager(token);
    this.twilio = await Twilio.create(token, { logLevel: 'info' });

    this.listenTokenExpiration();
    this.listenNewMessages();

    this.channelsState.update({ isInitializing: false });

    await this.requestSync();
  }

  listenTokenExpiration() {
    this.accessManager.on('tokenUpdated', ({ token }) =>
      this.twilio.updateToken(token),
    );

    this.accessManager.on('tokenExpired', async () => {
      const { token } = await this.mojito.getToken();
      this.accessManager.updateToken(token);
    });
  }

  getChannelsByState(channels, state) {
    return channels.filter(ch => ch.state === state);
  }

  setActiveChannel(reservationId) {
    this.activeChannel = reservationId;
    this.updateChannel(reservationId, ch => ({ ...ch, counter: 0 }));
  }

  listenNewMessages() {
    this.twilio.on('messageAdded', async (message) => {
      try {
        const channel = message.channel;
        const reservationId = channel.uniqueName;
        const isCurrentChat = this.activeChannel === reservationId;

        const author = await this.twilio.getUserDescriptor(message.author);
        this.requestSync();

        return this.updateChannel(reservationId, ch => ({
          ...ch,
          counter: isCurrentChat ? 0 : ch.counter + 1,
          lastMessage: message.body,
          lastMessageTimestamp: message.timestamp,
          lastMessageAuthor: author.friendlyName,
        }));
      } catch (error) {
        console.error('Update local state error: ', error);
        return null;
      }
    });
  }

  mergeStates(channels, reservations) {
    const reservationsIndex = _.keyBy(reservations, 'id');
    const channelsIndex = _.keyBy(channels, 'reservationId');

    const updatedChannels = channels
      .filter(ch => _.has(reservationsIndex, ch.reservationId))
      .map((ch) => {
        const {
          attributes: reservation,
        } = reservationsIndex[ch.reservationId];

        return {
          ...ch,
          state: this.getReservationState(reservation),
          guestName: [reservation.first_name, reservation.last_name].join(' '),
        };
      })
      .sort((l, r) => moment(r.lastMessageTimestamp).diff(l.lastMessageTimestamp));

    return {
      counters: TwilioController.getChannelCounters(updatedChannels),
      channels: updatedChannels,
      reservations,
      reservationsWithChats: Object.keys(channelsIndex),
    };
  }

  searchReservations(search, reservations) {
    const naturalComp = (l, r) =>
      l.localeCompare(r, undefined, {
        numeric: true,
        sensitivity: 'base',
      });

    if (!search.length) {
      return reservations
        .sort((left, right) => {
          const lr = _.get(left, 'attributes.room', '');
          const rr = _.get(right, 'attributes.room', '');

          return naturalComp(lr, rr);
        });
    }

    const searchIndex = new Fuse(reservations, {
      threshold: 0.2,
      keys: [
        'attributes.room',
        'attributes.name',
        'attributes.phone',
      ],
    });

    return searchIndex.search(search) || [];
  }

  static async getAllPages(request) {
    const page = await request;

    if (page.hasNextPage) {
      return [
        ...page.items,
        ...await TwilioController.getAllPages(page.nextPage()),
      ];
    }

    return page.items;
  }

  static getChannelCounters(channels) {
    return channels.reduce(
      (counters, channel) => {
        const isActive = channel.counter ? 1 : 0;

        return {
          ...counters,
          total: counters.total + isActive,
          [channel.state]: counters[channel.state] + isActive,
        };
      },
      { ...defaultCounters },
    );
  }

  isToday(date) {
    const tz = this.session.getAffiliateTimezone();
    return moment(date).tz(tz).isSame(moment().tz(tz), 'day');
  }

  getReservationState(reservation) {
    if (
      ['pre_checked_in', 'reserved', 'pending'].includes(reservation.state)
    ) {
      return 'due_in';
    }

    if (reservation.state === 'checked_in') {
      if (this.isToday(reservation.checkin_date)) {
        return 'checked_in';
      }
      if (this.isToday(reservation.checkout_date)) {
        return 'due_out';
      }

      return 'in_house';
    }

    return reservation.state;
  }

  static channelToModel(channel) {
    return {
      reservationId: channel.reservation_id,
      channelId: channel.channel_id,
    };
  }

  async getChannelData(channelId) {
    const channel = await this.twilio.getChannelBySid(channelId);
    const { items: [lastMessage = null] } = await channel.getMessages(1);
    let counter = await channel.getUnconsumedMessagesCount();

    if (counter === null) {
      counter = await channel.getMessagesCount();
    }

    const user = lastMessage && await this.twilio.getUserDescriptor(lastMessage.author);

    return {
      counter,
      lastMessage: _.get(lastMessage, 'body'),
      lastMessageTimestamp: _.get(lastMessage, 'timestamp') || _.get(channel, 'dateUpdated'),
      lastMessageAuthor: _.get(user, 'friendlyName'),
    };
  }

  async extendChannelsWithLastMessage(channelModels) {
    const channelsData = await Promise.all(
      channelModels.map(ch => this.getChannelData(ch.channelId)),
    );

    return channelModels.map((cm, index) => ({
      ...cm,
      ...channelsData[index],
    }));
  }

  async syncChannelCounters() {
    if (!this.isReady) {
      return;
    }

    const startTime = new Date();
    this.channelsState.update({ isLoading: true });

    try {
      const [reservations, mojitoChannels] = await Promise.all([
        this.gemsService.getAllReservations(),
        this.mojito.getChannels(),
      ]);

      const channels = await this.extendChannelsWithLastMessage(
        mojitoChannels.map(TwilioController.channelToModel),
      );

      this.channelsState.update({
        ready: true,
        isLoading: false,
        reservations,
        ...this.mergeStates(channels, reservations),
      });

      await this.channelsState.save();
      const dt = ((new Date() - startTime) / 1000).toFixed(3);
      console.log(`[Twilio] channels sync in ${dt} s`);
    } catch (error) {
      this.channelsState.update({ hasError: error, isLoading: false });
      console.error('ERROR', error);
    }
  }

  async ensureChatExists(reservationId) {
    const channel = await this.mojito.ensureChannelExist(reservationId);
    const { channels } = this.channelsState.value;

    const currentModel = _.find(channels, { channelId: channel.channelId });
    if (currentModel) {
      const newModel = {
        counter: 0,
        reservationId: channel.reservationId,
        channelId: channel.channelId,
        friendlyName: '',
        lastMessage: null,
        lastMessageTimestamp: Date.now(),
        lastMessageAuthor: null,
      };

      this.channelsState.update({
        channels: [...channels, newModel],
      });

      this.requestSync();
    }
  }

  updateChannel(reservationId, update) {
    const { channels, reservations } = this.channelsState.value;

    const updated = channels.map(
      ch => (ch.reservationId === reservationId ? update(ch) : ch),
    );

    this.channelsState.update({
      channels: updated,
      ...this.mergeStates(updated, reservations),
    });
    this.channelsState.save();
  }
}

inject(TwilioController, [
  'MojitoService',
  'StateFactory',
  'GEMSService',
  'SessionService',
]);
export default TwilioController;



// WEBPACK FOOTER //
// ./src/controllers/TwilioController.js